import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppContext from '../services/context';
import PageTemplate from '../components/PageTemplate';
import { availablePlans, contactAreas, formatPhoneBR, validateEmailFormat, validatePhoneFormat } from '../hooks/useUserProfile';
import clsx from 'clsx';
import { CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION } from '../config/legal';
import { trackEvent } from '../lib/analytics';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useError } from '../hooks/useError.js';
import { useToast } from '../hooks/useToast.js';

const INITIAL_FORM = {
  fullName: '',
  birthDate: '',
  area: '',
  email: '',
  phone: '',
  allowEmail: true,
  allowPhone: false,
  plan: 'PLUS',
  legalAccepted: false,
};

const getAllowedChannels = (form) => [
  form.allowEmail ? 'email' : null,
  form.allowPhone ? 'phone' : null,
].filter(Boolean);

const Signup = () => {
  const { userActions, userState, themeKey } = useContext(AppContext);
  const { showError, getErrorMessage } = useError();
  const toast = useToast();
  usePageTitle('Cadastro');
  const [form, setForm] = useState(INITIAL_FORM);
  const [authMode, setAuthMode] = useState('signup');
  const [step, setStep] = useState(() => (userState?.isAuthenticated ? 2 : 1));
  const [errors, setErrors] = useState({});
  const [pinValues, setPinValues] = useState({ email: '', phone: '' });
  const [message, setMessage] = useState({ text: '', variant: 'success' });
  const [loginChannel, setLoginChannel] = useState('phone');
  const [loginForm, setLoginForm] = useState({ email: '', phone: '' });
  const [loginPinSent, setLoginPinSent] = useState(false);
  const [signupCompleted, setSignupCompleted] = useState(false);
  const [signupStatus, setSignupStatus] = useState('');
  const [verificationState, setVerificationState] = useState({ email: false, phone: false });
  const location = useLocation();
  const navigate = useNavigate();
  const isDarkTheme = themeKey === 'dark';
  const isAccountCreated = Boolean(userState?.isAuthenticated || signupCompleted);
  const accountStatus = userState?.profile?.status || signupStatus;
  const verifiedContacts = userState?.profile?.verified || verificationState;
  const accountPlan = userState?.profile?.plan || form.plan;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get('mode') || params.get('modo');
    if (!isAccountCreated && modeParam === 'login') {
      setAuthMode('login');
      setStep(1);
    }
  }, [isAccountCreated, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plano');
    if (!isAccountCreated && authMode === 'signup' && planParam && availablePlans.includes(planParam.toUpperCase())) {
      setForm((current) => ({ ...current, plan: planParam.toUpperCase() }));
    }
  }, [authMode, isAccountCreated, location.search]);

  useEffect(() => {
    if (userState?.isAuthenticated) {
      setForm((current) => ({
        ...current,
        fullName: userState.profile.fullName ?? current.fullName,
        birthDate: userState.profile.birthDate ?? current.birthDate,
        area: userState.profile.area ?? current.area,
        email: userState.profile.email ?? current.email,
        phone: userState.profile.phone ?? current.phone,
        allowEmail: userState.profile.preferences?.email ?? current.allowEmail,
        allowPhone: userState.profile.preferences?.phone ?? current.allowPhone,
        plan: userState.profile.plan ?? current.plan,
      }));
      setStep(2);
    }
  }, [userState]);

  const handleChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'phone' ? { allowPhone: Boolean(String(value).trim()) } : {}),
    }));
    setErrors((current) => {
      if (!current[field] && !(field === 'phone' && current.contactPreferences)) return current;
      const nextErrors = { ...current };
      delete nextErrors[field];
      if (field === 'phone') delete nextErrors.contactPreferences;
      return nextErrors;
    });
  };

  const handleContactBlur = (field) => {
    const nextFieldError = field === 'email'
      ? (!validateEmailFormat(form.email) ? 'E-mail inválido (RN-05).' : '')
      : (() => {
          if (form.allowPhone && !form.phone) return 'Telefone é obrigatório para WhatsApp/SMS (RN-02).';
          if (form.phone && !validatePhoneFormat(form.phone)) return 'Telefone precisa ter DDD + número brasileiro válido (RN-06).';
          if (form.phone && !form.allowPhone) return 'Mantenha o recebimento de PIN por WhatsApp/SMS ativado para usar este telefone.';
          return '';
        })();

    setErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors[field];
      if (!nextFieldError) return nextErrors;
      return { ...nextErrors, [field]: nextFieldError };
    });
  };

  const handleSwitchAuthMode = (nextMode) => {
    setAuthMode(nextMode);
    setErrors({});
    setMessage({ text: '', variant: 'success' });
    setLoginPinSent(false);
  };

  const showMessage = (text, variant = 'success') => {
    const displayText = variant === 'error' ? getErrorMessage(text) : text;
    if (variant === 'error') {
      showError(text);
    } else if (displayText) {
      toast.success(displayText);
    }
    setMessage({ text: displayText || '', variant });
  };

  const clearMessage = () => {
    setMessage({ text: '', variant: 'success' });
  };

  const validatePersonalStep = () => {
    const nextErrors = {};
    if (!form.fullName) nextErrors.fullName = 'Nome é obrigatório (RN-01).';
    if (!form.birthDate) nextErrors.birthDate = 'Data de nascimento é obrigatória (RN-01).';
    if (!contactAreas.includes(form.area)) nextErrors.area = 'Área de atuação inválida (RN-03).';
    setErrors(nextErrors);
    return nextErrors;
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.fullName) nextErrors.fullName = 'Nome é obrigatório (RN-01).';
    if (!form.birthDate) nextErrors.birthDate = 'Data de nascimento é obrigatória (RN-01).';
    if (!contactAreas.includes(form.area)) nextErrors.area = 'Área de atuação inválida (RN-03).';
    if (!validateEmailFormat(form.email)) nextErrors.email = 'E-mail inválido (RN-05).';
    if (!availablePlans.includes(form.plan)) nextErrors.plan = 'Plano inválido (RN-04).';
    if (form.allowPhone && !form.phone) nextErrors.phone = 'Telefone é obrigatório para WhatsApp/SMS (RN-02).';
    if (form.phone && !validatePhoneFormat(form.phone)) nextErrors.phone = 'Telefone precisa ter DDD + número brasileiro válido (RN-06).';
    if (form.phone && !form.allowPhone) nextErrors.phone = 'Mantenha o recebimento de PIN por WhatsApp/SMS ativado para usar este telefone.';
    if (!form.allowEmail && !form.allowPhone) nextErrors.contactPreferences = 'Autorize pelo menos um canal (RN-08).';
    if (form.legalAccepted !== true) nextErrors.legalAccepted = 'Aceite os Termos de Uso e a Política de Privacidade para criar a conta.';
    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isAccountCreated) return;
    const validation = validate();
    if (Object.keys(validation).length) {
      trackEvent('form_error', {
        form_name: 'signup',
        step_name: 'account_create',
        error_count: Object.keys(validation).length,
        error_fields: Object.keys(validation),
      });
      return;
    }

    const result = await userActions.createAccount(form);
    if (!result.success) {
      trackEvent('form_error', {
        form_name: 'signup',
        step_name: 'account_create',
        error_count: Object.keys(result.errors || {}).length || 1,
        error_code: 'signup_failed',
      });
      setErrors(result.errors || {});
      if (result.message) {
        showMessage(result.message, 'error');
      }
      return;
    }
    setSignupCompleted(true);
    setSignupStatus('pending');
    setVerificationState({ email: false, phone: false });
    trackEvent('sign_up', {
      method: getAllowedChannels(form).join(','),
      plan: form.plan,
      allowed_channels_count: getAllowedChannels(form).length,
    });
    showMessage(result.message || 'Conta criada com status pendente de ativação. Selecione um canal e solicite o PIN.');
  };

  const handleRequestPin = async (channel) => {
    const targetValue = channel === 'email' ? form.email : form.phone;
    const result = await userActions.requestValidationPin(channel, targetValue);
    trackEvent('request_pin', {
      flow_name: 'signup_activation',
      channel,
      success: result.success,
    });
    showMessage(result.message, result.success ? 'success' : 'error');
    if (result.verified) {
      setVerificationState(result.verified);
    }
    if (result.status) {
      setSignupStatus(result.status);
    }
  };

  const handleValidatePin = async (channel) => {
    if (!pinValues[channel]) return;
    const targetValue = authMode === 'signup'
      ? (channel === 'email' ? form.email : form.phone)
      : undefined;
    const result = await userActions.verifyPin(channel, pinValues[channel], targetValue);
    trackEvent('verify_pin', {
      flow_name: authMode === 'signup' ? 'signup_activation' : 'login',
      channel,
      success: result.success,
      account_status: result.status || null,
    });
    showMessage(result.message, result.success ? 'success' : 'error');

    if (result.verified) {
      setVerificationState(result.verified);
    }
    if (result.status) {
      setSignupStatus(result.status);
      if (result.status === 'active' && authMode === 'signup' && !userState?.isAuthenticated) {
        setAuthMode('login');
        setLoginPinSent(false);
        setPinValues({ email: '', phone: '' });
        setLoginChannel(form.allowPhone ? 'phone' : 'email');
        setLoginForm({
          email: form.email,
          phone: form.phone
        });
        showMessage('Cadastro validado. Solicite o PIN para entrar.');
      }
    }

    if (authMode === 'login' && result.success) {
      trackEvent('login', {
        method: channel,
      });
      navigate('/');
    }
  };

  const handleLoginRequestPin = async () => {
    const nextErrors = {};
    if (loginChannel === 'email' && !validateEmailFormat(loginForm.email)) {
      nextErrors.loginEmail = 'E-mail inválido.';
    }
    if (loginChannel === 'phone' && !validatePhoneFormat(loginForm.phone)) {
      nextErrors.loginPhone = 'Telefone precisa ter DDD + número.';
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      trackEvent('form_error', {
        form_name: 'login',
        step_name: 'request_pin',
        error_count: Object.keys(nextErrors).length,
        error_fields: Object.keys(nextErrors),
      });
      showMessage('Corrija os dados de login antes de solicitar o PIN.', 'error');
      return;
    }

    setErrors({});
    clearMessage();

    const result = await userActions.requestLoginPin({
      channel: loginChannel,
      email: loginForm.email,
      phone: loginForm.phone,
    });

    if (!result.success) {
      trackEvent('request_pin', {
        flow_name: 'login',
        channel: loginChannel,
        success: false,
      });
      setErrors(result.errors || {});
      showMessage(result.message || 'Erro ao solicitar PIN.', 'error');
      return;
    }

    setErrors({});
    setLoginPinSent(true);
    trackEvent('request_pin', {
      flow_name: 'login',
      channel: loginChannel,
      success: true,
    });
    showMessage(result.code ? `${result.message} Código temporário: ${result.code}` : result.message);
  };

  const cardStyle = useMemo(
    () =>
      clsx(
        'border rounded-2xl p-4 space-y-4',
        isDarkTheme
          ? 'bg-[#0f3a1b] border-greenbook-dark-primary/40 text-greenbook-dark-text shadow-sm'
          : 'bg-white border-gray-200 shadow-sm'
      ),
    [isDarkTheme]
  );

  const inputClassName = clsx(
    'w-full border rounded-lg px-3 py-2 outline-none transition-colors',
    isDarkTheme
      ? 'bg-[#0a2e14] text-greenbook-dark-text border-greenbook-dark-primary/30 placeholder:text-gray-400 focus:border-greenbook-dark-primary focus:ring-2 focus:ring-greenbook-dark-primary/25'
      : 'bg-white text-gray-900 border-gray-300 placeholder:text-gray-500 focus:border-greenbook-light-primary focus:ring-2 focus:ring-greenbook-light-primary/20'
  );

  const selectClassName = clsx(inputClassName, 'min-h-[42px] appearance-none pr-12');

  const primaryButtonClassName = clsx(
    'inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    isDarkTheme
      ? 'bg-greenbook-dark-primary text-greenbook-dark-toggle-text hover:bg-greenbook-light-green focus-visible:outline-greenbook-dark-primary'
      : 'bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900 focus-visible:outline-greenbook-light-primary'
  );

  const loginPinActionClassName = 'w-full px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors';

  const secondaryButtonClassName = clsx(
    'inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold transition-colors border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    isDarkTheme
      ? 'bg-gray-950/20 text-greenbook-dark-text border-gray-700 hover:border-greenbook-dark-primary focus-visible:outline-greenbook-dark-primary'
      : 'bg-white text-gray-900 border-gray-300 hover:border-greenbook-light-primary focus-visible:outline-greenbook-light-primary'
  );

  const shouldShowEmailPin = Boolean(isAccountCreated && form.allowEmail);
  const shouldShowPhonePin = Boolean(isAccountCreated && form.allowPhone);

  const pinSectionClassName = clsx(
    'rounded-xl border p-3 space-y-2',
    isDarkTheme
      ? 'border-greenbook-dark-primary/45 bg-[#0a2e14]'
      : 'border-greenbook-light-primary/25 bg-greenbook-light-green/10'
  );

  return (
    <PageTemplate showSearchInput={false} alwaysShowLogo>
      <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Cadastro e ativação</h1>
          <p className="text-sm text-gray-500 dark:text-gray-300">
            Preencha seus dados, escolha o plano e confirme um canal com PIN para ativar a conta.
          </p>
          {authMode === 'signup' && (
            <p className="text-xs text-gray-500 dark:text-gray-300">
              Etapa {step} de 2 {accountStatus ? `• Status: ${accountStatus}` : ''}
            </p>
          )}
        </div>

        {!isAccountCreated && (
          <div className="flex justify-center">
            <div
              className={clsx(
                'inline-flex rounded-xl border p-1 gap-1',
                isDarkTheme ? 'border-greenbook-dark-primary/40 bg-[#0a2e14]' : 'border-gray-200 bg-white'
              )}
            >
              <button
                type="button"
                className={clsx(
                  'px-4 py-2 text-sm font-semibold rounded-lg transition-colors',
                  authMode === 'signup'
                    ? isDarkTheme
                      ? 'bg-greenbook-dark-primary text-greenbook-dark-toggle-text'
                      : 'bg-greenbook-light-primary text-white'
                    : isDarkTheme
                    ? 'text-greenbook-dark-text hover:bg-greenbook-dark-green/40'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
                onClick={() => handleSwitchAuthMode('signup')}
              >
                Criar conta
              </button>
              <button
                type="button"
                className={clsx(
                  'px-4 py-2 text-sm font-semibold rounded-lg transition-colors',
                  authMode === 'login'
                    ? isDarkTheme
                      ? 'bg-greenbook-dark-primary text-greenbook-dark-toggle-text'
                      : 'bg-greenbook-light-primary text-white'
                    : isDarkTheme
                    ? 'text-greenbook-dark-text hover:bg-greenbook-dark-green/40'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
                onClick={() => handleSwitchAuthMode('login')}
              >
                Entrar
              </button>
            </div>
          </div>
        )}

        {authMode === 'login' ? (
          <div className={cardStyle}>
            <h2 className="font-semibold text-lg">Login</h2>
            <p className="text-sm text-gray-600 dark:text-gray-200">
              Informe seu contato e valide o PIN para entrar.
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                className={clsx(
                  'flex-1 px-4 py-2 rounded-lg font-semibold border transition-colors',
                  loginChannel === 'phone'
                    ? isDarkTheme
                      ? 'border-greenbook-dark-primary bg-greenbook-dark-green/40 text-greenbook-dark-text'
                      : 'border-greenbook-light-primary bg-greenbook-light-green/20 text-gray-900'
                    : isDarkTheme
                    ? 'border-greenbook-dark-primary/30 text-greenbook-dark-text hover:border-greenbook-dark-primary'
                    : 'border-gray-300 text-gray-900 hover:border-greenbook-light-primary'
                )}
                onClick={() => { setLoginChannel('phone'); setLoginPinSent(false); clearMessage(); }}
              >
                Via WhatsApp/SMS
              </button>
              <button
                type="button"
                className={clsx(
                  'flex-1 px-4 py-2 rounded-lg font-semibold border transition-colors',
                  loginChannel === 'email'
                    ? isDarkTheme
                      ? 'border-greenbook-dark-primary bg-greenbook-dark-green/40 text-greenbook-dark-text'
                      : 'border-greenbook-light-primary bg-greenbook-light-green/20 text-gray-900'
                    : isDarkTheme
                    ? 'border-greenbook-dark-primary/30 text-greenbook-dark-text hover:border-greenbook-dark-primary'
                    : 'border-gray-300 text-gray-900 hover:border-greenbook-light-primary'
                )}
                onClick={() => { setLoginChannel('email'); setLoginPinSent(false); clearMessage(); }}
              >
                Via e-mail
              </button>
            </div>

            {loginChannel === 'email' ? (
              <div className="text-sm space-y-1">
                <span>E-mail</span>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm((current) => ({ ...current, email: e.target.value }))}
                  className={inputClassName}
                  placeholder="seuemail@exemplo.com"
                  disabled={loginPinSent}
                />
                {errors.loginEmail && <p className="text-xs text-red-600">{errors.loginEmail}</p>}
              </div>
            ) : (
              <div className="text-sm space-y-1">
                <span>Telefone (com DDD)</span>
                <input
                  type="tel"
                  value={loginForm.phone}
                  onChange={(e) => setLoginForm((current) => ({ ...current, phone: formatPhoneBR(e.target.value) }))}
                  inputMode="numeric"
                  maxLength={16}
                  className={inputClassName}
                  placeholder="(71) 99999-9999"
                  disabled={loginPinSent}
                />
                {errors.loginPhone && <p className="text-xs text-red-600">{errors.loginPhone}</p>}
              </div>
            )}

            {!loginPinSent ? (
              <button
                type="button"
                className={clsx('w-full', primaryButtonClassName)}
                onClick={handleLoginRequestPin}
              >
                Enviar PIN
              </button>
            ) : (
              <div className={pinSectionClassName}>
                <p className="text-sm font-semibold">Digite o PIN</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pinValues[loginChannel]}
                    onChange={(e) => setPinValues({ ...pinValues, [loginChannel]: e.target.value })}
                    className={inputClassName}
                    placeholder="Código"
                  />
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 font-semibold hover:bg-red-100 transition-colors"
                    onClick={() => { setLoginPinSent(false); setPinValues({ email: '', phone: '' }); clearMessage(); }}
                  >
                    Cancelar
                  </button>
                </div>
                <button
                  type="button"
                  className={loginPinActionClassName}
                  onClick={() => handleValidatePin(loginChannel)}
                  disabled={!pinValues[loginChannel]}
                >
                  Validar
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {step === 1 && (
            <>
              <div className={cardStyle}>
                <h2 className="font-semibold text-lg">Dados pessoais</h2>
                <label className="text-sm space-y-1 block">
                  Nome completo*
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    className={inputClassName}
                    required
                  />
                  {errors.fullName && <p className="text-xs text-red-600">{errors.fullName}</p>}
                </label>
                <label className="text-sm space-y-1 block">
                  Data de nascimento*
                  <input
                    type="date"
                    value={form.birthDate}
                    onChange={(e) => handleChange('birthDate', e.target.value)}
                    className={inputClassName}
                    required
                  />
                  {errors.birthDate && <p className="text-xs text-red-600">{errors.birthDate}</p>}
                </label>
                <label className="text-sm space-y-1 block">
                  Área de atuação*
                  <div className="relative">
                    <select
                      value={form.area}
                      onChange={(e) => handleChange('area', e.target.value)}
                      className={selectClassName}
                      required
                    >
                      <option value="">Selecione</option>
                      {contactAreas.map((area) => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                    <svg
                      aria-hidden="true"
                      className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  {errors.area && <p className="text-xs text-red-600">{errors.area}</p>}
                </label>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className={primaryButtonClassName}
                  onClick={() => {
                    const validation = validatePersonalStep();
                    if (Object.keys(validation).length) {
                      trackEvent('form_error', {
                        form_name: 'signup',
                        step_name: 'personal_data',
                        error_count: Object.keys(validation).length,
                        error_fields: Object.keys(validation),
                      });
                      return;
                    }
                    setErrors({});
                    trackEvent('signup_step_complete', {
                      step_name: 'personal_data',
                      step_number: 1,
                    });
                    setStep(2);
                  }}
                >
                  Próximo
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={cardStyle}>
                  <h2 className="font-semibold text-lg">Dados de contato</h2>
                  <div className="space-y-3">
                    <div className="text-sm space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span>E-mail*</span>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={form.allowEmail}
                            onChange={(e) => handleChange('allowEmail', e.target.checked)}
                            disabled={isAccountCreated}
                          />
                          <span>Receber PIN por e-mail</span>
                        </label>
                      </div>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        onBlur={() => handleContactBlur('email')}
                        className={inputClassName}
                        required
                        disabled={isAccountCreated}
                      />
                      {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                    </div>

                    {shouldShowEmailPin && (
                      <div className={pinSectionClassName}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">Ativar por e-mail</p>
                          {verifiedContacts.email && (
                            <span className="text-xs text-green-600">Verificado</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                          onClick={() => handleRequestPin('email')}
                        >
                          Enviar PIN por e-mail
                        </button>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={pinValues.email}
                            onChange={(e) => setPinValues({ ...pinValues, email: e.target.value })}
                            className={inputClassName}
                            placeholder="Digite o código"
                          />
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors"
                            onClick={() => handleValidatePin('email')}
                            disabled={!pinValues.email}
                          >
                            Validar
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="text-sm space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span>WhatsApp/SMS (telefone com DDD)</span>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={form.allowPhone}
                            onChange={(e) => handleChange('allowPhone', e.target.checked)}
                            required={Boolean(form.phone.trim())}
                            disabled={isAccountCreated}
                          />
                          <span>Receber PIN por WhatsApp/SMS</span>
                        </label>
                      </div>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => handleChange('phone', formatPhoneBR(e.target.value))}
                        inputMode="numeric"
                        maxLength={16}
                        onBlur={() => handleContactBlur('phone')}
                        className={inputClassName}
                        placeholder="(71) 99999-9999"
                        required={form.allowPhone}
                        disabled={isAccountCreated}
                      />
                      {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
                    </div>

                    {shouldShowPhonePin && (
                      <div className={pinSectionClassName}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">Ativar por WhatsApp/SMS</p>
                          {verifiedContacts.phone && (
                            <span className="text-xs text-green-600">Verificado</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                          onClick={() => handleRequestPin('phone')}
                        >
                          Enviar PIN por WhatsApp/SMS
                        </button>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={pinValues.phone}
                            onChange={(e) => setPinValues({ ...pinValues, phone: e.target.value })}
                            className={inputClassName}
                            placeholder="Digite o código"
                          />
                          <button
                            type="button"
                            className="px-3 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors"
                            onClick={() => handleValidatePin('phone')}
                            disabled={!pinValues.phone}
                          >
                            Validar
                          </button>
                        </div>
                      </div>
                    )}

                    {!isAccountCreated && (
                      <>
                        {errors.contactPreferences && (
                          <p className="text-xs text-red-600">{errors.contactPreferences}</p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-300">
                          Um dos canais acima deve estar autorizado para envio do PIN.
                        </p>
                      </>
                    )}

                    {isAccountCreated && !shouldShowEmailPin && !shouldShowPhonePin && (
                      <p className="text-xs text-amber-700 dark:text-amber-200">
                        Nenhum canal está autorizado para envio de PIN. Faça logout e cadastre novamente selecionando um canal (RN-08).
                      </p>
                    )}
                  </div>
                </div>

                <div className={cardStyle}>
                  <h2 className="font-semibold text-lg">Escolha de plano</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {availablePlans.map((plan) => (
                      <button
                        type="button"
                        key={plan}
                        onClick={() => handleChange('plan', plan)}
                        className={clsx(
                          'border rounded-xl p-3 text-left transition-colors',
                          form.plan === plan
                            ? 'border-greenbook-light-primary bg-greenbook-light-green/20 dark:border-greenbook-dark-primary dark:bg-greenbook-dark-green/30'
                            : 'border-gray-200 bg-white/60 hover:border-greenbook-light-primary dark:border-greenbook-dark-primary/30 dark:bg-[#0a2e14] dark:hover:border-greenbook-dark-primary'
                        )}
                        disabled={isAccountCreated}
                      >
                        <p className="font-semibold">{plan === 'PLUS' ? 'Plus (Early Access)' : 'Free'}</p>
                        <p className="text-xs text-gray-700 dark:text-gray-200">
                          {plan === 'PLUS' ? 'Desbloqueia busca semântica e IA temporariamente grátis.' : 'Busca básica gratuita.'}
                        </p>
                      </button>
                    ))}
                  </div>
                  {errors.plan && <p className="text-xs text-red-600">{errors.plan}</p>}
                  {!isAccountCreated && (
                    <label className="mt-4 flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={form.legalAccepted}
                        onChange={(e) => handleChange('legalAccepted', e.target.checked)}
                      />
                      <span>
                        Li e aceito os{' '}
                        <Link to="/termos" className="underline" target="_blank" rel="noreferrer">Termos de Uso</Link>{' '}
                        e a{' '}
                        <Link to="/politica-de-privacidade" className="underline" target="_blank" rel="noreferrer">Política de Privacidade</Link>{' '}
                        vigentes (Termos {CURRENT_TERMS_VERSION}; Privacidade {CURRENT_PRIVACY_VERSION}).
                      </span>
                    </label>
                  )}
                  {errors.legalAccepted && <p className="text-xs text-red-600">{errors.legalAccepted}</p>}
                  {!isAccountCreated && (
                    <button type="submit" className={clsx('mt-3 w-full', primaryButtonClassName)}>
                      Criar conta
                    </button>
                  )}
                  {isAccountCreated && (
                    <p className="text-xs text-gray-500 dark:text-gray-300 mt-3">
                      Conta já criada. Solicite e valide o PIN no canal autorizado para concluir o cadastro.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-between gap-2">
                <button type="button" className={secondaryButtonClassName} onClick={() => setStep(1)}>
                  Voltar
                </button>
                {accountStatus === 'active' ? (
                  <Link to="/" className={clsx('text-center', primaryButtonClassName)}>
                    Ir para a busca
                  </Link>
                ) : (
                  <button type="button" className={secondaryButtonClassName} onClick={() => showMessage('Solicite e valide o PIN para concluir o cadastro.', 'error')}>
                    Precisa de ajuda?
                  </button>
                )}
              </div>
            </>
          )}
          </form>
        )}

        {message.text && (
          <div className={clsx(
            'rounded-xl border px-4 py-3 text-sm',
            message.variant === 'error'
              ? isDarkTheme
                ? 'border-red-400/50 bg-red-950/40 text-red-100'
                : 'border-red-200 bg-red-50 text-red-800'
              : isDarkTheme
              ? 'border-greenbook-dark-primary/40 bg-greenbook-dark-green text-green-50'
              : 'border-green-200 bg-greenbook-light-green text-green-900'
          )}>
            {message.text}
          </div>
        )}

        {isAccountCreated && (
          <div className={cardStyle}>
            <h3 className="font-semibold">Resumo da conta</h3>
            <ul className="text-sm space-y-1">
              <li>Status: {accountStatus || 'pending'}</li>
              <li>Plano: {accountPlan}</li>
              <li>Canal e-mail: {verifiedContacts.email ? 'Verificado' : 'Pendente'}</li>
              <li>Canal telefone: {verifiedContacts.phone ? 'Verificado' : 'Pendente'}</li>
            </ul>
          </div>
        )}
      </div>
    </PageTemplate>
  );
};

export default Signup;
